import React from "react";
import { formatMomentDate } from "../../../utils/dateHelper";
import { useNavigate } from "react-router-dom";
import StatusChip from "../../../components/statusChip/StatusChip";
import CustomTable from "../../../components/table/CustomTable";
import { routes } from "../../../routes/pageRoutes";

const BulkOfferTableSection = ({
    data,
    isLoading,
    isInitialLoad,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    onToggleActive,
    togglingId,
    canUpdate,
    actionList
}) => {
    const navigate = useNavigate();

    const getStatusBg = (status) => {
        const lowerStatus = status?.toLowerCase();
        switch (lowerStatus) {
            case "pending":
                return "warning";
            case "approved":
                return "success";
            case "published":
                return "blue";
            case "rejected":
                return "danger";
            default:
                return "";
        }
    };

    const pagination = {
        currentPage,
        totalPages: Math.ceil((totalItems || 0) / (pageSize || 10)),
        total: totalItems || 0,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: pageSize || 10,
        setPageSize,
        setCurrentPage,
        perPageOptions: [10, 20, 50, 100],
    };

    const columns = [
        {
            id: "company",
            header: "COMPANY",
            cell: (_, row) => {
                const company = row?.company || {};
                const companyLogo = company?.profile?.logo?.url;
                return (
                    <div className="d-flex align-items-center gap-2">
                        {companyLogo ? (
                            <img src={companyLogo} alt="Logo" width={30} height={30} className="rounded-circle object-fit-cover" />
                        ) : (
                            <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: 30, height: 30, fontSize: 10 }}>Logo</div>
                        )}
                        <div className="d-flex flex-column text-start">
                            <span className="fw-medium text-nowrap">{company.name || "-"}</span>
                            <span className="text-secondary small">{company?.profile?.mobile_number || "-"}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            id: "offer_name",
            header: "OFFER NAME",
            cell: (_, row) => {
                const aPlusFirstPhoto = row?.a_plus_photos?.[0]?.url;
                return (
                    <div className="d-flex align-items-center gap-2">
                        {aPlusFirstPhoto ? (
                            <img src={aPlusFirstPhoto} alt="Offer" width={30} height={30} className="rounded object-fit-cover" />
                        ) : (
                            <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 30, height: 30, fontSize: 10 }}>Img</div>
                        )}
                        <span className="text-nowrap">{row.name || "-"}</span>
                    </div>
                );
            }
        },
        {
            id: "category",
            header: "CATEGORY",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.category?.name || "-"}
                </span>
            )
        },
        {
            id: "sku",
            header: "SKU",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_product_details?.sku || "-"}
                </span>
            )
        },
        {
            id: "variants",
            header: "VARIANTS",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_product_details?.variations || "-"}
                </span>
            )
        },
        {
            id: "country",
            header: "COUNTRY OF ORIGIN",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_product_details?.country?.name || "-"}
                </span>
            )
        },
        {
            id: "state",
            header: "STATE",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.state?.name || "-"}
                </span>
            )
        },
        {
            id: "city",
            header: "CITY",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.city?.name || "-"}
                </span>
            )
        },
        {
            id: "serviceability",
            header: "SERVICEABILITY",
            cell: (_, row) => (
                <span className="small text-nowrap text-capitalize">
                    {row?.company_offer_details?.serviceability_type?.replace(/_/g, " ") || "-"}
                </span>
            )
        },
        {
            id: "mrp",
            header: "MRP",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_offer_details?.mrp ? `₹ ${parseFloat(row.company_offer_details.mrp).toLocaleString()}` : "-"}
                </span>
            )
        },
        {
            id: "offer_price",
            header: "OFFER PRICE",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_offer_details?.offer_price ? `₹ ${parseFloat(row.company_offer_details.offer_price).toLocaleString()}` : "-"}
                </span>
            )
        },
        {
            id: "lot_size",
            header: "LOT SIZE",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_offer_details?.lot_size || "-"}
                </span>
            )
        },
        {
            id: "available_qty",
            header: "AVAILABLE QTY",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_offer_details?.available_qty || "-"}
                </span>
            )
        },
        {
            id: "booking_deposit",
            header: "BOOKING DEPOSIT",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.company_offer_details?.booking_deposit ? `₹ ${parseFloat(row.company_offer_details.booking_deposit).toLocaleString()}` : "-"}
                </span>
            )
        },
        {
            id: "status",
            header: "STATUS",
            cell: (_, row) => row?.status ? (
                <StatusChip
                    bg={getStatusBg(row.status)}
                    pill
                    title={row.status}
                    className="text-capitalize"
                />
            ) : "-"
        },
        {
            id: "is_active",
            header: "ACTIVE",
            isSwitch: true,
            checked: (row) => row?.is_active,
            onSwitchChange: (checked, row) => onToggleActive(row.id, checked),
            isDisabled: (row) => togglingId === row.id || !canUpdate,
        },
        {
            id: "created_at",
            header: "CREATED AT",
            cell: (_, row) => (
                <span className="small text-nowrap">
                    {row?.created_at ? formatMomentDate(row.created_at, "DD MMM YYYY, hh:mm A") : "-"}
                </span>
            )
        }
    ];

    return (
        <CustomTable
            data={data || []}
            columns={columns}
            loading={isLoading && !isInitialLoad}
            emptyMessage="No bulk offers found"
            keyField="id"
            pagination={pagination}
            actionList={actionList}
            rowClick={(row) =>
                navigate(routes.viewMarketplaceBulkOffer.replace(":id", row.id), { state: { currentPage } })
            }
        />
    );
};

export default BulkOfferTableSection;
